<template>
	<button type="submit"
	        class="flex justify-center items-center"
	        :class="
				{
					// sizes
					'h-7 font-normal rounded px-2 py-1 leading-6 text-md': props.size === ButtonSize.EndpointTable,
					'h-8 font-medium rounded px-2 py-2 leading-6': props.size === ButtonSize.Dev,
					'h-10 font-medium rounded px-4 py-2.5 leading-6': props.size === ButtonSize.ExtraSmall,
					'h-11 font-medium rounded px-6 py-2.5 leading-6': props.size === ButtonSize.Small,
					'h-12 text-lg font-semibold rounded px-5 py-3 leading-6': props.size === ButtonSize.Default,

					//default style
					'bg-primary text-label-on-color hover:bg-primary-subtle-hover': props.type === ButtonType.Default && !props.disabled,
					'bg-primary text-label-on-color bg-opacity-70 text-opacity-70': props.type === ButtonType.Default && props.disabled,

					//outline style
					'bg-neutral text-primary-darker outline-0 border border-primary-dark hover:bg-primary-darker hover:border-primary-darker hover:text-label-on-color': props.type === ButtonType.Outline && !props.disabled,
					'bg-neutral text-primary-darker outline-0 border border-primary-dark bg-opacity-50 text-opacity-50 border-opacity-50': props.type === ButtonType.Outline && props.disabled,

					//warning style
					'bg-error text-label-on-color hover:outline-0 hover:bg-neutral hover:text-error hover:border hover:border-error': props.type === ButtonType.Warning && !props.disabled,
					'bg-error text-label-on-color bg-opacity-70 text-opacity-70': props.type === ButtonType.Warning && props.disabled,
				}
				"
	        :disabled="props.disabled || props.loading"
	        @click.prevent="onButtonClick">

		<span class="relative flex">
		<span class="absolute w-full flex justify-center mt-0.5 animate-spin" v-if="props.loading">
			<svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
				<mask id="path-1-inside-1_1881_16183" fill="currentColor">
					<path
						d="M15.328 23.5293C17.8047 22.8144 19.9853 21.321 21.547 19.2701C23.1087 17.2193 23.9686 14.72 23.9992 12.1424C24.0297 9.56481 23.2295 7.04587 21.7169 4.95853C20.2043 2.8712 18.0597 1.32643 15.6007 0.552947C13.1417 -0.220538 10.499 -0.181621 8.0638 0.663935C5.62864 1.50949 3.53049 3.11674 2.07999 5.24771C0.629495 7.37868 -0.096238 9.92009 0.0102418 12.4957C0.116722 15.0713 1.04975 17.5441 2.6712 19.5481L4.96712 17.6904C3.74474 16.1796 3.04133 14.3154 2.96106 12.3737C2.88079 10.432 3.42791 8.51604 4.52142 6.90953C5.61493 5.30301 7.19671 4.09133 9.03255 3.45387C10.8684 2.81642 12.8607 2.78708 14.7145 3.3702C16.5683 3.95332 18.1851 5.1179 19.3254 6.69152C20.4658 8.26514 21.0691 10.1641 21.046 12.1074C21.023 14.0506 20.3748 15.9347 19.1974 17.4809C18.02 19.027 16.3761 20.1528 14.5089 20.6918L15.328 23.5293Z" />
				</mask>
				<path
					d="M15.328 23.5293C17.8047 22.8144 19.9853 21.321 21.547 19.2701C23.1087 17.2193 23.9686 14.72 23.9992 12.1424C24.0297 9.56481 23.2295 7.04587 21.7169 4.95853C20.2043 2.8712 18.0597 1.32643 15.6007 0.552947C13.1417 -0.220538 10.499 -0.181621 8.0638 0.663935C5.62864 1.50949 3.53049 3.11674 2.07999 5.24771C0.629495 7.37868 -0.096238 9.92009 0.0102418 12.4957C0.116722 15.0713 1.04975 17.5441 2.6712 19.5481L4.96712 17.6904C3.74474 16.1796 3.04133 14.3154 2.96106 12.3737C2.88079 10.432 3.42791 8.51604 4.52142 6.90953C5.61493 5.30301 7.19671 4.09133 9.03255 3.45387C10.8684 2.81642 12.8607 2.78708 14.7145 3.3702C16.5683 3.95332 18.1851 5.1179 19.3254 6.69152C20.4658 8.26514 21.0691 10.1641 21.046 12.1074C21.023 14.0506 20.3748 15.9347 19.1974 17.4809C18.02 19.027 16.3761 20.1528 14.5089 20.6918L15.328 23.5293Z"
					stroke="currentColor" stroke-width="14" mask="url(#path-1-inside-1_1881_16183)" />
			</svg>
		</span>

      <span :class="labelClass">
        {{ props.label }}
      </span>
    </span>
	</button>
</template>

<script setup lang="js">

import { ButtonSize, ButtonType } from '@/components/Forms/Buttons/SubmitButtonConstants.js'
import { computed } from 'vue'

const emit = defineEmits(['click'])

const props = defineProps(
	{
		type: {
			type: String,
			default: ButtonType.Default,
			validator(value)
			{
				return ButtonType.isValid(value)
			}
		},
		size: {
			type: String,
			default: ButtonSize.Default,
			validator(value)
			{
				return ButtonSize.isValid(value)
			}
		},
		label: { type: String, default: '' },
		disabled: { type: Boolean, default: false },
		loading: { type: Boolean, default: false }
	})

const labelClass = computed(() =>
{
	const classes = [];
	if (props.loading)
	{
		classes.push('invisible');
	}

	if (props.size === ButtonSize.Dev)
	{
		classes.push('text-sm')
	}

	return classes.join(' ')
})

function onButtonClick(e)
{
	if (!props.disabled && !props.loading)
	{
		emit('click', e)
	}
}
</script>
